/**
 * Shared helpers for calendar-only dates (field_start_date,
 * planned_field_finish_date, swim_lane_due_date, week_ending, ...).
 */

const MS_PER_DAY = 1000 * 60 * 60 * 24;

const DEFAULT_FORMAT_OPTIONS = {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
};

function formatYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** Today's date as YYYY-MM-DD in the browser's local timezone. */
export function getTodayCalendarYmd() {
  return formatYmd(new Date());
}

/**
 * Normalize a date value to YYYY-MM-DD for comparison.
 * Pass API strings in raw — don't pre-parse a date-only string with
 * `new Date(value)` first, or it will already be a day early.
 * @param {string|Date|null|undefined} value
 * @returns {string|null}
 */
export function parseCalendarYmd(value) {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'string') {
    const m = value.trim().match(/^(\d{4}-\d{2}-\d{2})/);
    if (m) return m[1];
  }
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) return null;
  return formatYmd(d);
}

/**
 * Parse a calendar date as local noon (stable across DST; avoids UTC
 * midnight drift). Same as toCalendarDate, except empty/invalid input
 * returns an Invalid Date instead of null, so existing callers that chain
 * `.toLocaleDateString()` keep working.
 * @param {string|Date|null|undefined} value
 * @returns {Date}
 */
export function parseLocalCalendarDate(value) {
  return toCalendarDate(value) ?? new Date(NaN);
}

/**
 * Convert a calendar date value to a local-noon Date (for date pickers,
 * chart axes, and anything else that needs a Date object).
 * @param {string|Date|null|undefined} value
 * @returns {Date|null}
 */
export function toCalendarDate(value) {
  const ymd = parseCalendarYmd(value);
  if (!ymd) return null;
  const [y, m, d] = ymd.split('-').map(Number);
  const date = new Date(y, m - 1, d, 12, 0, 0);
  // Reject impossible dates like 2026-02-31 instead of rolling them over.
  if (formatYmd(date) !== ymd) return null;
  return date;
}

/**
 * Format a calendar date for display, e.g. "Feb 10, 2026".
 * @param {string|Date|null|undefined} value
 * @param {Intl.DateTimeFormatOptions} [options]
 * @returns {string|null} null when the value is empty or invalid
 */
export function formatCalendarDate(value, options = DEFAULT_FORMAT_OPTIONS) {
  const date = toCalendarDate(value);
  if (!date) return null;
  return date.toLocaleDateString('en-US', options);
}

/** True if calendar date a is strictly before b. False if either is empty. */
export function isCalendarDateBefore(a, b) {
  const ymdA = parseCalendarYmd(a);
  const ymdB = parseCalendarYmd(b);
  if (!ymdA || !ymdB) return false;
  return ymdA < ymdB;
}

/**
 * Whole calendar days from `from` to `to` (negative when `to` is earlier).
 * DST-safe: computed on UTC day numbers, not local millisecond differences.
 * @returns {number|null} null if either value is empty or invalid
 */
export function calendarDaysBetween(from, to) {
  const a = toCalendarDate(from);
  const b = toCalendarDate(to);
  if (!a || !b) return null;
  const utcA = Date.UTC(a.getFullYear(), a.getMonth(), a.getDate());
  const utcB = Date.UTC(b.getFullYear(), b.getMonth(), b.getDate());
  return Math.round((utcB - utcA) / MS_PER_DAY);
}

/**
 * Add calendar days to a calendar date; returns YYYY-MM-DD in local timezone.
 * @returns {string|null} null if the input is empty or invalid
 */
export function addCalendarDays(value, days) {
  const d = toCalendarDate(value);
  if (!d) return null;
  d.setDate(d.getDate() + days);
  return parseCalendarYmd(d);
}

/** Monday (YYYY-MM-DD) of the current local calendar week (Mon–Sun). */
export function getCurrentWeekMondayYmd() {
  const today = new Date();
  const day = today.getDay(); // 0 = Sunday
  const diff = today.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(today.getFullYear(), today.getMonth(), diff);
  return parseCalendarYmd(monday);
}
